import React from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmationModal from '@/components/common/ConfirmationModal/ConfirmationModal';
import useMoveTracksToVolume from '@/hooks/useMoveTracksVolume';
import { useErrorContext } from '@/context/ErrorContext';
import { ITrack } from '@/data/models/track';
import useGetAlbumQuery from '@/hooks/useGetAlbumQuery';

interface Props {
  show: boolean;
  onClose: () => void;
  usesSides: boolean;
  volumeNumbers: number[];
  onAddVolume: (volumeNumber: number) => void;
  albumId: string;
  selectedTracks: ITrack[];
  allTracks: ITrack[];
  reloadTracks: () => Promise<void>;
}

export default function MoveTracksToNewVolumeConfirmationModal({
  show,
  onClose,
  usesSides,
  volumeNumbers,
  onAddVolume,
  albumId,
  selectedTracks,
  allTracks,
  reloadTracks,
}: Props) {
  const { t } = useTranslation();
  const { data: album } = useGetAlbumQuery(albumId);
  const { setError } = useErrorContext();
  const { moveTracks } = useMoveTracksToVolume();

  const handleMoveToNewVolumeConfirm = () => {
    if (!album) return;

    const newVolumeNumber =
      volumeNumbers.length > 0 ? Math.max(...volumeNumbers) + 1 : 1;

    moveTracks(
      albumId,
      album,
      allTracks,
      selectedTracks,
      newVolumeNumber,
      async () => {
        if (volumeNumbers.length > 0) {
          onAddVolume(volumeNumbers[volumeNumbers.length - 1]);
        }
        await reloadTracks();
        onClose();
      },
      () => {
        setError(t('common.error.defaultError'));
        onClose();
      }
    );
  };

  if (!show) return null;

  return (
    <ConfirmationModal
      showModal={show}
      setShowModal={(s: boolean) => {
        if (!s) onClose();
      }}
      message={
        usesSides
          ? t('product.moveTracksToVolumeModal.moveToNewSideBody')
          : t('product.moveTracksToVolumeModal.moveToNewDiscBody')
      }
      header={
        usesSides
          ? t('product.moveTracksToVolumeModal.moveToNewSideHeader')
          : t('product.moveTracksToVolumeModal.moveToNewDiscHeader')
      }
      handleConfirm={handleMoveToNewVolumeConfirm}
    />
  );
}
